import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { logger } from "#app/monitoring/Logger";
import { partQuery } from "#modules/parts/infra/part.query";
import { AnalyzeTypeType, AssemblySplitModeType, PartTypeType } from "shared";

import { checkPart, CheckPartDto } from "../infra/checkPart.api";

type Options = {
  onSuccess?: (part: CheckPartDto | null) => void;
  onMutate?: () => void;
  onError?: (error: Error) => void;
};

export const useCheckPart = (options?: Options) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: ({
      id,
      force,
      analyzeType,
      partType,
      shouldUpdatePartType,
      shouldPredictAfterAnalyze,
      quoteRequestLineId,
      assemblySplitMode,
    }: {
      id: string;
      force?: boolean;
      analyzeType?: AnalyzeTypeType;
      partType?: PartTypeType;
      shouldUpdatePartType?: boolean;
      shouldPredictAfterAnalyze?: boolean;
      quoteRequestLineId?: string;
      assemblySplitMode?: AssemblySplitModeType;
    }) =>
      checkPart(id, {
        force,
        analyzeType,
        partType,
        shouldUpdatePartType,
        shouldPredictAfterAnalyze,
        quoteRequestLineId,
        assemblySplitMode,
      }),
    onMutate: () => {
      options?.onMutate?.();
    },
    onSuccess: (part) => {
      void queryClient.invalidateQueries(partQuery(part?.id));
      options?.onSuccess?.(part);
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        logger.error("Error when trying to check part analyze", error);
      }
      options?.onError?.(error);
    },
  });

  return { checkPart: mutate, ...rest };
};
